/* eslint-disable no-useless-escape */
/* eslint-disable no-unused-vars */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import MaterialSelector from './MaterialSelector';

const SupplierForm = ({
  formData,
  errors,
  onInputChange,
  onToggleMaterial,
  onRatingChange,
  showRatings = false,
  isLoading = false,
  onSave,
  onCancel,
  onDelete = null,
  saveButtonText = 'Save',
  showDeleteButton = false,
}) => {
  // Validation functions
  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = phone => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const calculateOverallRating = () => {
    if (!showRatings || !formData.ratings) return 0;
    const { quality, reliability, cost, time } = formData.ratings;
    return ((quality + reliability + cost + time) / 4).toFixed(1);
  };

  const renderFormField = (label, field, placeholder, options = {}) => (
    <View style={styles.formField}>
      <Text style={styles.fieldLabel}>
        {label}
        {options.required && <Text style={styles.requiredStar}> *</Text>}
      </Text>
      <TextInput
        style={[
          styles.textInput,
          errors[field] && styles.errorInput,
          options.multiline && styles.multilineInput,
        ]}
        placeholder={placeholder}
        placeholderTextColor="#999"
        value={formData[field]}
        onChangeText={value => onInputChange(field, value)}
        keyboardType={options.keyboardType || 'default'}
        multiline={options.multiline || false}
        numberOfLines={options.numberOfLines || 1}
        autoCapitalize={options.autoCapitalize || 'sentences'}
        autoCorrect={options.autoCorrect !== false}
      />
      {errors[field] && <Text style={styles.errorText}>{errors[field]}</Text>}
    </View>
  );

  const renderRatingSlider = (label, ratingType) => {
    if (!showRatings || !formData.ratings) return null;

    const value = formData.ratings[ratingType];
    return (
      <View style={styles.ratingField}>
        <View style={styles.ratingHeader}>
          <Text style={styles.ratingLabel}>{label}</Text>
          <Text style={styles.ratingValue}>{value.toFixed(1)}</Text>
        </View>
        <View style={styles.ratingSlider}>
          {[1, 2, 3, 4, 5].map(rating => (
            <TouchableOpacity
              key={rating}
              style={[
                styles.ratingButton,
                value >= rating && styles.activeRatingButton,
              ]}
              onPress={() => onRatingChange(ratingType, rating)}
            >
              <Ionicons
                name={value >= rating ? 'star' : 'star-outline'}
                size={24}
                color={value >= rating ? '#ffd700' : '#ddd'}
              />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  const renderStatusSelector = () => (
    <View style={styles.formField}>
      <Text style={styles.fieldLabel}>Status</Text>
      <View style={styles.statusContainer}>
        <TouchableOpacity
          style={[
            styles.statusButton,
            formData.status === 'Active' && styles.activeStatusButton,
          ]}
          onPress={() => onInputChange('status', 'Active')}
        >
          <Text
            style={[
              styles.statusButtonText,
              formData.status === 'Active' && styles.activeStatusButtonText,
            ]}
          >
            Active
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.statusButton,
            formData.status === 'Inactive' && styles.inactiveStatusButton,
          ]}
          onPress={() => onInputChange('status', 'Inactive')}
        >
          <Text
            style={[
              styles.statusButtonText,
              formData.status === 'Inactive' && styles.inactiveStatusButtonText,
            ]}
          >
            Inactive
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={styles.formContainer}>
      {/* Basic Information Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Basic Information</Text>

        {renderFormField(
          'Supplier Name',
          'name',
          'Enter supplier company name',
          { required: true, autoCapitalize: 'words' }
        )}

        {renderFormField(
          'Contact Person',
          'contactPerson',
          'Primary contact person name',
          { autoCapitalize: 'words' }
        )}

        {renderFormField('Phone Number', 'phone', '+44 20 1234 5678', {
          required: true,
          keyboardType: 'phone-pad',
        })}

        {renderFormField('Email Address', 'email', 'contact@supplier.com', {
          required: true,
          keyboardType: 'email-address',
          autoCapitalize: 'none',
        })}

        {renderFormField('Website', 'website', 'https://www.supplier.com', {
          keyboardType: 'url',
          autoCapitalize: 'none',
        })}
      </View>

      {/* Location Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Location</Text>

        {renderFormField(
          'Address/Location',
          'location',
          'City, Country or full address',
          { required: true, autoCapitalize: 'words' }
        )}
      </View>

      {/* Materials & Services Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Materials & Services</Text>
        <MaterialSelector
          selectedMaterials={formData.materials || []}
          onToggleMaterial={onToggleMaterial}
          error={errors.materials}
        />
      </View>

      {/* Ratings Section - Only show in edit mode */}
      {showRatings && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Performance Ratings</Text>
          <Text style={styles.fieldDescription}>
            Rate this supplier's performance (1-5 stars)
          </Text>

          <View style={styles.overallRatingDisplay}>
            <Text style={styles.overallRatingLabel}>Overall Rating</Text>
            <Text style={styles.overallRatingValue}>
              {calculateOverallRating()}
            </Text>
          </View>

          {renderRatingSlider('Quality', 'quality')}
          {renderRatingSlider('Reliability', 'reliability')}
          {renderRatingSlider('Cost Effectiveness', 'cost')}
          {renderRatingSlider('Delivery Time', 'time')}
        </View>
      )}

      {/* Company Details Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Company Details</Text>

        {renderFormField(
          'Company Size',
          'companySize',
          'e.g., 1-10, 11-50, 51-200, 200+ employees'
        )}

        {renderFormField(
          'Years in Business',
          'yearsInBusiness',
          'e.g., 5 years, 10+ years',
          { keyboardType: 'numeric' }
        )}

        {renderStatusSelector()}
      </View>

      {/* Notes Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Additional Information</Text>

        {renderFormField(
          'Notes',
          'notes',
          'Add any additional notes about this supplier...',
          { multiline: true, numberOfLines: 4 }
        )}
      </View>

      {/* Action Buttons */}
      <View style={styles.actionButtons}>
        {showDeleteButton && onDelete && (
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={onDelete}
            disabled={isLoading}
          >
            <MaterialCommunityIcons name="delete" size={20} color="#fff" />
            <Text style={styles.deleteButtonText}>Delete</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.cancelButton}
          onPress={onCancel}
          disabled={isLoading}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.saveActionButton,
            isLoading && styles.disabledActionButton,
          ]}
          onPress={onSave}
          disabled={isLoading}
        >
          {isLoading ? (
            <MaterialCommunityIcons name="loading" size={20} color="#fff" />
          ) : (
            <Ionicons name="checkmark" size={20} color="#fff" />
          )}
          <Text style={styles.saveActionButtonText}>
            {isLoading ? 'Processing...' : saveButtonText}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 10,
  },
  formField: {
    marginBottom: 20,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  requiredStar: {
    color: '#e74c3c',
  },
  fieldDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    color: '#333',
  },
  multilineInput: {
    height: 100,
    textAlignVertical: 'top',
  },
  errorInput: {
    borderColor: '#e74c3c',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 14,
    marginTop: 5,
  },
  statusContainer: {
    flexDirection: 'row',
    gap: 10,
  },
  statusButton: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  activeStatusButton: {
    backgroundColor: '#27ae60',
    borderColor: '#27ae60',
  },
  inactiveStatusButton: {
    backgroundColor: '#e74c3c',
    borderColor: '#e74c3c',
  },
  statusButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  activeStatusButtonText: {
    color: '#fff',
  },
  inactiveStatusButtonText: {
    color: '#fff',
  },
  overallRatingDisplay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
  },
  overallRatingLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  overallRatingValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  ratingField: {
    marginBottom: 15,
  },
  ratingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  ratingLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  ratingValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
  },
  ratingSlider: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
  },
  ratingButton: {
    padding: 5,
  },
  activeRatingButton: {
    // Additional styling for active rating if needed
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
    marginBottom: 40,
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingVertical: 15,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
  },
  saveActionButton: {
    flex: 1,
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  disabledActionButton: {
    opacity: 0.5,
  },
  saveActionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default SupplierForm;
